import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  FileText,
  Upload,
  Download,
  Trash2,
  Eye,
  Plus,
  Search,
  Filter,
  Calendar,
  Building2,
  DollarSign,
  Receipt,
  Shield,
  FileSpreadsheet,
  Calculator,
  Loader2,
  ArrowLeft,
  CheckCircle2,
  Clock,
  FolderOpen,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  levy_statement: Receipt,
  bank_statement: Building2,
  insurance: Shield,
  annual_financials: FileSpreadsheet,
  agm_minutes: FileText,
  maintenance_report: FileText,
  budget: Calculator,
  audit_report: Search,
  other: FolderOpen,
};

const CATEGORY_COLORS = {
  levy_statement: 'bg-emerald-100 text-emerald-700',
  bank_statement: 'bg-blue-100 text-blue-700',
  insurance: 'bg-purple-100 text-purple-700',
  annual_financials: 'bg-amber-100 text-amber-700',
  agm_minutes: 'bg-sky-100 text-sky-700',
  maintenance_report: 'bg-orange-100 text-orange-700',
  budget: 'bg-indigo-100 text-indigo-700',
  audit_report: 'bg-rose-100 text-rose-700',
  other: 'bg-slate-100 text-slate-700',
};

export default function AgentFinancials() {
  const { hoaId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [building, setBuilding] = useState(null);
  
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: '',
    period: '',
    visibility: 'trustees',
    file: null,
  });

  const token = localStorage.getItem('session_token');

  useEffect(() => {
    fetchData();
  }, [hoaId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch categories
      const catRes = await fetch(`${API_URL}/api/financials/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (catRes.ok) {
        const catData = await catRes.json();
        setCategories(catData.categories || []);
      }

      // Fetch documents
      const docsRes = await fetch(`${API_URL}/api/financials/list/${hoaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (docsRes.ok) {
        const docsData = await docsRes.json();
        setDocuments(docsData);
      }

      // Fetch summary
      const sumRes = await fetch(`${API_URL}/api/financials/summary/${hoaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (sumRes.ok) {
        const sumData = await sumRes.json();
        setSummary(sumData);
      }

      // Fetch building info
      const buildRes = await fetch(`${API_URL}/api/managing-agents/building/${hoaId}/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (buildRes.ok) {
        const buildData = await buildRes.json();
        setBuilding(buildData.building);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Demo data
      setCategories([
        { id: 'levy_statement', name: 'Levy Statement' },
        { id: 'bank_statement', name: 'Bank Statement' },
        { id: 'insurance', name: 'Insurance Certificate' },
        { id: 'annual_financials', name: 'Annual Financials' },
        { id: 'agm_minutes', name: 'AGM Minutes' },
        { id: 'maintenance_report', name: 'Maintenance Report' },
        { id: 'budget', name: 'Budget Document' },
        { id: 'audit_report', name: 'Audit Report' },
        { id: 'other', name: 'Other Financial' },
      ]);
      setDocuments([
        { document_id: '1', title: 'March 2026 Levy Statement', category: 'levy_statement', period: 'March 2026', visibility: 'trustees', uploaded_by_name: 'Sarah Johnson', created_at: new Date().toISOString() },
        { document_id: '2', title: 'Q1 2026 Bank Statement', category: 'bank_statement', period: 'Q1 2026', visibility: 'trustees', uploaded_by_name: 'Sarah Johnson', created_at: new Date().toISOString() },
      ]);
      setSummary({ total_documents: 2, by_category: { levy_statement: 1, bank_statement: 1 } });
      setBuilding({ name: 'Demo Building', hoa_id: hoaId });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm({ ...uploadForm, file });
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.title || !uploadForm.category || !uploadForm.file) {
      toast.error('Please fill in all required fields');
      return;
    }

    setUploading(true);
    try {
      // In a real implementation, you would upload the file to cloud storage first
      // For demo, we'll simulate with a mock URL
      const mockFileUrl = `https://storage.bodycorp365.com/financials/${Date.now()}_${uploadForm.file.name}`;
      
      const response = await fetch(`${API_URL}/api/financials/upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          hoa_id: hoaId,
          title: uploadForm.title,
          description: uploadForm.description,
          category: uploadForm.category,
          period: uploadForm.period,
          visibility: uploadForm.visibility,
          file_url: mockFileUrl,
          file_type: uploadForm.file.name.split('.').pop(),
          file_size: uploadForm.file.size,
        })
      });

      if (response.ok) {
        toast.success('Document uploaded successfully');
        setShowUploadDialog(false);
        setUploadForm({
          title: '',
          description: '',
          category: '',
          period: '',
          visibility: 'trustees',
          file: null,
        });
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await fetch(`${API_URL}/api/financials/${documentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Document deleted');
        fetchData();
      } else {
        toast.error('Failed to delete document');
      }
    } catch (error) {
      toast.error('Error deleting document');
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         doc.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || categoryId;
  };

  const CategoryIcon = ({ category }) => {
    const Icon = CATEGORY_ICONS[category] || FolderOpen;
    return <Icon className="w-5 h-5" />;
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8" data-testid="agent-financials">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <Button 
              variant="ghost" 
              className="mb-2 -ml-2 text-slate-600"
              onClick={() => navigate(`/agent/building/${hoaId}`)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to {building?.name || 'Building'}
            </Button>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Financial Documents</h1>
            <p className="text-slate-500 mt-1">Upload and manage financial documents for trustees</p>
          </div>
          <Button 
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={() => setShowUploadDialog(true)}
            data-testid="upload-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Document
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{summary?.total_documents || 0}</p>
                  <p className="text-sm text-slate-500">Total Documents</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{summary?.by_category?.levy_statement || 0}</p>
                  <p className="text-sm text-slate-500">Levy Statements</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{summary?.by_category?.bank_statement || 0}</p>
                  <p className="text-sm text-slate-500">Bank Statements</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <FileSpreadsheet className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{summary?.by_category?.annual_financials || 0}</p>
                  <p className="text-sm text-slate-500">Annual Reports</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Documents List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-slate-200 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : filteredDocuments.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No documents found</h3>
              <p className="text-slate-500 mb-4">Upload your first financial document to get started</p>
              <Button onClick={() => setShowUploadDialog(true)} className="bg-indigo-600 hover:bg-indigo-700">
                <Upload className="w-4 h-4 mr-2" />
                Upload Document
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredDocuments.map(doc => (
              <Card key={doc.document_id} className="hover:shadow-md transition-shadow" data-testid={`doc-${doc.document_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${CATEGORY_COLORS[doc.category] || 'bg-slate-100'}`}>
                        <CategoryIcon category={doc.category} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{doc.title}</h3>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <Badge variant="secondary">{getCategoryName(doc.category)}</Badge>
                          {doc.period && (
                            <span className="text-sm text-slate-500 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {doc.period}
                            </span>
                          )}
                          <span className="text-sm text-slate-400">
                            by {doc.uploaded_by_name}
                          </span>
                        </div>
                        {doc.description && (
                          <p className="text-sm text-slate-500 mt-2">{doc.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={doc.visibility === 'all_residents' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                        {doc.visibility === 'all_residents' ? 'Public' : 'Trustees'}
                      </Badge>
                      <Button variant="ghost" size="icon" className="text-slate-500 hover:text-indigo-600">
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-slate-500 hover:text-indigo-600">
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="text-slate-500 hover:text-red-600"
                        onClick={() => handleDelete(doc.document_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Upload Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Upload Financial Document</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Document Title *</Label>
                <Input
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  placeholder="e.g., March 2026 Levy Statement"
                  className="mt-1"
                  data-testid="upload-title"
                />
              </div>
              <div>
                <Label>Category *</Label>
                <Select 
                  value={uploadForm.category} 
                  onValueChange={(v) => setUploadForm({ ...uploadForm, category: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="upload-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Period</Label>
                <Input
                  value={uploadForm.period}
                  onChange={(e) => setUploadForm({ ...uploadForm, period: e.target.value })}
                  placeholder="e.g., March 2026, Q1 2026, FY 2025-2026"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  placeholder="Optional description..."
                  className="mt-1"
                  rows={3}
                />
              </div>
              <div>
                <Label>Visibility</Label>
                <Select 
                  value={uploadForm.visibility} 
                  onValueChange={(v) => setUploadForm({ ...uploadForm, visibility: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trustees">Trustees Only</SelectItem>
                    <SelectItem value="owners">Owners & Trustees</SelectItem>
                    <SelectItem value="all_residents">All Residents</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>File *</Label>
                <div className="mt-1 border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-indigo-300 transition-colors">
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                    accept=".pdf,.xlsx,.xls,.docx,.doc,.csv"
                    data-testid="file-input"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    {uploadForm.file ? (
                      <div className="flex items-center justify-center gap-2 text-indigo-600">
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="font-medium">{uploadForm.file.name}</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm text-slate-600">Click to upload or drag and drop</p>
                        <p className="text-xs text-slate-400 mt-1">PDF, Excel, Word (max 10MB)</p>
                      </>
                    )}
                  </label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={handleUpload}
                disabled={uploading}
                data-testid="submit-upload"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
